import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';


const QuoteItemsList = ({ 
  items = [], 
  onEditItem,
  onSaveEdit,
  onRemoveItem,
  onRequestPriceChange,
  onViewSupplierQuotes,
  authorizedItemIds = null,
  onAuthorizationChange = null,
}) => {
  const [editingItemId, setEditingItemId] = useState(null);
  const [draftValues, setDraftValues] = useState({});

  const calculateTotals = () => {
    const totalCost = items?.reduce((sum, item) => sum + (item?.cost * item?.quantity), 0);
    const totalPrice = items?.reduce((sum, item) => sum + (item?.price * item?.quantity), 0);
    const totalMargin = totalCost > 0 ? ((totalPrice - totalCost) / totalCost * 100) : 0;
    return { totalCost, totalPrice, totalMargin };
  };

  const { totalCost, totalPrice, totalMargin } = calculateTotals();

  const getTypeIcon = (type) => {
    const icons = {
      service: 'Wrench',
      part: 'Package',
      tire: 'Circle',
      labor: 'Users',
      other: 'Box'
    };
    return icons?.[type] || 'Box';
  };

  const getTypeLabel = (type) => {
    const labels = {
      service: 'Servicio',
      part: 'Refacción',
      tire: 'Llanta',
      labor: 'Mano de Obra',
      other: 'Otro'
    };
    return labels?.[type] || type;
  };

  const handleEditClick = (item) => {
    if (editingItemId === item?.id) {
      setEditingItemId(null);
      setDraftValues({});
    } else {
      setEditingItemId(item?.id);
      setDraftValues({ ...item });
    }
  };

  const handleDraftChange = (field, value) => {
    setDraftValues(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = () => {
    const cost = parseFloat(draftValues?.cost) || 0;
    const price = parseFloat(draftValues?.price) || 0;
    const quantity = parseInt(draftValues?.quantity) || 1;
    const margin = cost > 0 ? ((price - cost) / cost * 100) : 0;
    const updatedItem = {
      ...draftValues,
      cost,
      price,
      quantity,
      margin
    };
    if (onSaveEdit) {
      onSaveEdit(updatedItem);
    } else if (onEditItem) {
      onEditItem(updatedItem);
    }
    setEditingItemId(null);
    setDraftValues({});
  };

  const handleCancel = () => {
    setEditingItemId(null);
    setDraftValues({});
  };

  if (items?.length === 0) {
    return (
      <div className="bg-card rounded-lg border border-border p-8 text-center">
        <Icon name="ShoppingCart" size={48} className="text-muted-foreground mx-auto mb-4" />
        <h3 className="text-lg font-heading font-semibold text-foreground mb-2">
          No hay items en la cotización
        </h3>
        <p className="text-sm caption text-muted-foreground">
          Agregue items desde las sugerencias o manualmente
        </p>
      </div>
    );
  }

  const showAuthCheckboxes = authorizedItemIds !== null && onAuthorizationChange !== null;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Icon name="FileText" size={24} className="text-primary" />
          <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground">
            Items de Cotización
          </h2>
        </div>
        <span className="text-sm caption text-muted-foreground">
          {items?.length} items
        </span>
      </div>
      <div className="space-y-3">
        {items?.map((item, index) => {
          const isAuthorized = showAuthCheckboxes ? authorizedItemIds?.has(item?.id) : true;
          const isEditing = editingItemId === item?.id;
          const draft = isEditing ? draftValues : {};
          const draftCost = parseFloat(draft?.cost) || 0;
          const draftPrice = parseFloat(draft?.price) || 0;
          const draftMargin = draftCost > 0 ? ((draftPrice - draftCost) / draftCost * 100) : 0;
          const draftMarginColor = draftMargin >= 30 ? 'text-success' : draftMargin >= 15 ? 'text-warning' : 'text-error';

          return (
            <div
              key={item?.id}
              className={`bg-card rounded-lg border transition-smooth ${
                isEditing ? 'border-primary shadow-elevation-2' : 'border-border hover:shadow-elevation-1'
              } ${
                showAuthCheckboxes && !isAuthorized ? 'opacity-50' : ''
              }`}
            >
              {/* Item summary row */}
              <div className="p-4">
                <div className="flex flex-col lg:flex-row lg:items-start gap-4">
                  {showAuthCheckboxes && (
                    <div className="flex items-center gap-2 flex-shrink-0 pt-1">
                      <input
                        type="checkbox"
                        id={`auth-${item?.id}`}
                        checked={isAuthorized}
                        onChange={(e) => onAuthorizationChange(item?.id, e?.target?.checked)}
                        className="w-4 h-4 rounded border-border text-primary cursor-pointer accent-primary"
                      />
                      <label
                        htmlFor={`auth-${item?.id}`}
                        className="text-xs font-medium text-muted-foreground cursor-pointer select-none whitespace-nowrap"
                      >
                        Autorizar
                      </label>
                    </div>
                  )}
                  <div className="flex items-start gap-3 flex-1 min-w-0">
                    <div className="bg-primary/10 p-2 rounded-lg flex-shrink-0">
                      <Icon name={getTypeIcon(item?.type)} size={20} className="text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <div className="flex-1 min-w-0">
                          <h3 className="text-base font-heading font-semibold text-foreground mb-1">
                            {item?.description}
                          </h3>
                          <span className="inline-block px-2 py-1 rounded text-xs caption bg-muted text-muted-foreground">
                            {getTypeLabel(item?.type)}
                          </span>
                        </div>
                        <span className="text-sm caption text-muted-foreground flex-shrink-0">
                          #{index + 1}
                        </span>
                      </div>

                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
                        <div>
                          <p className="text-xs caption text-muted-foreground mb-1">Cantidad</p>
                          <p className="text-sm font-body font-semibold text-foreground data-text">
                            {item?.quantity}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs caption text-muted-foreground mb-1">Costo Unit.</p>
                          <p className="text-sm font-body font-semibold text-foreground data-text">
                            ${item?.cost?.toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs caption text-muted-foreground mb-1">Precio Unit.</p>
                          <p className="text-sm font-body font-semibold text-foreground data-text">
                            ${item?.price?.toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs caption text-muted-foreground mb-1">Margen</p>
                          <p className={`text-sm font-body font-bold data-text ${
                            item?.margin >= 30 ? 'text-success' : 
                            item?.margin >= 15 ? 'text-warning': 'text-error'
                          }`}>
                            {item?.margin?.toFixed(2)}%
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center justify-between mt-3 pt-3 border-t border-border">
                        <div className="flex items-center gap-4">
                          <div>
                            <p className="text-xs caption text-muted-foreground">Costo Total</p>
                            <p className="text-sm font-body font-semibold text-foreground data-text">
                              ${(item?.cost * item?.quantity)?.toLocaleString('es-MX', { minimumFractionDigits: 2 })} MXN
                            </p>
                          </div>
                          <div>
                            <p className="text-xs caption text-muted-foreground">Precio Total</p>
                            <p className="text-base font-heading font-bold text-primary data-text">
                              ${(item?.price * item?.quantity)?.toLocaleString('es-MX', { minimumFractionDigits: 2 })} MXN
                            </p>
                          </div>
                        </div>
                        {item?.supplier && (
                          <div className="flex items-center gap-2">
                            <Icon name="Package" size={14} className="text-muted-foreground" />
                            <span className="text-xs caption text-muted-foreground">
                              {item?.supplier}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex lg:flex-col gap-2 flex-shrink-0">
                    <Button
                      variant={isEditing ? 'default' : 'outline'}
                      size="sm"
                      iconName="Edit"
                      onClick={() => handleEditClick(item)}
                      className="flex-1 lg:flex-none"
                    >
                      {isEditing ? 'Cerrar' : 'Editar'}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      iconName="DollarSign"
                      onClick={() => onRequestPriceChange(item)}
                      className="flex-1 lg:flex-none"
                    >
                      Precio
                    </Button>
                    {onViewSupplierQuotes && (
                      <Button
                        variant="outline"
                        size="sm"
                        iconName="Package"
                        onClick={() => onViewSupplierQuotes(item)}
                        className="flex-1 lg:flex-none"
                      >
                        Cotizaciones
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      iconName="Trash2"
                      onClick={() => onRemoveItem(item?.id)}
                      className="flex-1 lg:flex-none text-error hover:text-error"
                    >
                      Eliminar
                    </Button>
                  </div>
                </div>
              </div>

              {/* Inline edit expansion */}
              {isEditing && (
                <div className="border-t border-primary/30 bg-primary/5 p-4 rounded-b-lg">
                  <div className="flex items-center gap-2 mb-4">
                    <Icon name="Edit" size={16} className="text-primary" />
                    <span className="text-sm font-heading font-semibold text-primary">Editando ítem</span>
                  </div>

                  <div className="space-y-3">
                    {/* Fields for part/tire/service/other */}
                    {(item?.type === 'part' || item?.type === 'tire' || item?.type === 'service' || item?.type === 'other') && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-muted-foreground mb-1">Proveedor</label>
                          <input
                            type="text"
                            value={draft?.supplier || ''}
                            onChange={(e) => handleDraftChange('supplier', e?.target?.value)}
                            placeholder="Proveedor"
                            className="w-full px-3 py-2 text-sm bg-background border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary/50 text-foreground"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-muted-foreground mb-1">Categoría</label>
                          <input
                            type="text"
                            value={draft?.category || ''}
                            onChange={(e) => handleDraftChange('category', e?.target?.value)}
                            placeholder="Categoría"
                            className="w-full px-3 py-2 text-sm bg-background border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary/50 text-foreground"
                          />
                        </div>
                      </div>
                    )}

                    {/* Fields for labor */}
                    {item?.type === 'labor' && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-muted-foreground mb-1">Categoría</label>
                          <input
                            type="text"
                            value={draft?.category || ''}
                            onChange={(e) => handleDraftChange('category', e?.target?.value)}
                            placeholder="Categoría"
                            className="w-full px-3 py-2 text-sm bg-background border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary/50 text-foreground"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-muted-foreground mb-1">Horas</label>
                          <input
                            type="number"
                            min="0"
                            step="0.5"
                            value={draft?.hours || ''}
                            onChange={(e) => handleDraftChange('hours', e?.target?.value)}
                            placeholder="0"
                            className="w-full px-3 py-2 text-sm bg-background border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary/50 text-foreground"
                          />
                        </div>
                      </div>
                    )}

                    {/* Common numeric fields */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-muted-foreground mb-1">Cantidad</label>
                        <input
                          type="number"
                          min="1"
                          value={draft?.quantity || 1}
                          onChange={(e) => handleDraftChange('quantity', e?.target?.value)}
                          className="w-full px-3 py-2 text-sm bg-background border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary/50 text-foreground"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-muted-foreground mb-1">Costo Unitario (MXN)</label>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={draft?.cost || ''}
                          onChange={(e) => handleDraftChange('cost', e?.target?.value)}
                          placeholder="0.00"
                          className="w-full px-3 py-2 text-sm bg-background border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary/50 text-foreground"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-muted-foreground mb-1">Precio Unitario (MXN)</label>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={draft?.price || ''}
                          onChange={(e) => handleDraftChange('price', e?.target?.value)}
                          placeholder="0.00"
                          className="w-full px-3 py-2 text-sm bg-background border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary/50 text-foreground"
                        />
                      </div>
                    </div>

                    {/* Live margin preview */}
                    {draftCost > 0 && draftPrice > 0 && (
                      <div className="bg-muted/50 rounded-lg p-3 flex items-center justify-between">
                        <span className="text-xs caption text-muted-foreground">Margen calculado:</span>
                        <span className={`text-sm font-heading font-bold data-text ${draftMarginColor}`}>
                          {draftMargin?.toFixed(2)}%
                        </span>
                      </div>
                    )}

                    {/* Action buttons */}
                    <div className="flex gap-2 pt-1">
                      <Button
                        variant="default"
                        size="sm"
                        iconName="Save"
                        onClick={handleSave}
                        className="flex-1"
                      >
                        Guardar cambios
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        iconName="X"
                        onClick={handleCancel}
                        className="flex-1"
                      >
                        Cancelar
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
      <div className="bg-primary/5 rounded-lg border border-primary/20 p-6 mt-6">
        <h3 className="text-lg font-heading font-semibold text-foreground mb-4">
          Resumen de Cotización
        </h3>
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm caption text-muted-foreground">Costo Total:</span>
            <span className="text-lg font-body font-bold text-foreground data-text">
              ${totalCost?.toLocaleString('es-MX', { minimumFractionDigits: 2 })} MXN
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm caption text-muted-foreground">Precio Total:</span>
            <span className="text-xl font-heading font-bold text-primary data-text">
              ${totalPrice?.toLocaleString('es-MX', { minimumFractionDigits: 2 })} MXN
            </span>
          </div>
          <div className="flex items-center justify-between pt-3 border-t border-border">
            <span className="text-sm caption text-muted-foreground">Margen Promedio:</span>
            <span className={`text-lg font-heading font-bold data-text ${
              totalMargin >= 30 ? 'text-success' : 
              totalMargin >= 15 ? 'text-warning': 'text-error'
            }`}>
              {totalMargin?.toFixed(2)}%
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuoteItemsList;